import React, { Fragment, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import ReactRenderer from '../modules/_react-renderer'
import Translator from './components/translator'
import { CartNavigation } from './components/cart/cartNavigation'
import { CartContentTitle } from './components/cart/cartContentTitle'
import { CartContentProducts } from './components/cart/cartContentProducts'
import { CartOverview } from './components/shared/cartOverview'
import Settings from './components/settings'
import {StepController} from "./datastores/stepController";
import { RootStore } from './datastores/rootStore'
import GA4Tracking from "./utils/ga4-tracking";
import { ValidateAddToCartController } from "./datastores/validateAddToCartController";

const CartView = observer(function CartView(props) {

  Settings.setSettings(props.settings, 'cart')
  Translator.setTranslations(props.translations, 'cart')

  Settings.setSettings(props.cart_overview.settings, 'cart-overview')
  Translator.setTranslations(props.cart_overview.translations, 'cart-overview')

  Settings.setSettings(props.cart_navigation.settings, 'cart-navigation')
  Translator.setTranslations(props.cart_navigation.translations, 'cart-navigation')

  Translator.setTranslations(props.participants_dialog_translations, 'participant')
  Translator.setTranslations(props.type_validation_translations, 'type-validation')

  const [showModal, setShowModal] = React.useState(false)
  const [showLoadingModal, setShowLoadingModal] = React.useState(false)
  const [loadingModalText, setLoadingModalText] = React.useState('')

  let cart = RootStore.getInstance().cart

  useEffect(() => {
    if (props.hasOwnProperty('add_to_cart_direct_event') && props.add_to_cart_direct_event !== null) {
      cart.empty()

      // Set the loading modal text
      setLoadingModalText(Translator.translate('Arrangementet %s er ved at blive tilføjet til kurven, vent venligst', 'cart')
        .replace('%s', '<strong>' + props?.add_to_cart_direct_event?.title + '</strong>'))

      setShowLoadingModal(true)

      const addToCart = async () => {
        const eventId = props.add_to_cart_direct_event.id
        const response = await ValidateAddToCartController.getInstance()
          .validate([eventId])

        // If the event is not in the response, we show an error
        if (! response.hasOwnProperty(eventId)) {
          setLoadingModalText(Translator.translate('Der er sket en fejl', 'cart'))

          return new Promise((resolve) => resolve(false))
        }

        // If the event is valid, add it to the cart
        if (response[eventId].valid) {
          setLoadingModalText('')

          await cart.addEventGroups([props.add_to_cart_direct_event], false)
        }

        let errors = Object.values(response[eventId]?.errors ?? {})

        if(errors.length === 0) {
          return
        }

        let errorText = Translator.translate('Der er sket følgende fejl', 'cart') + ':<br><br>'

        errors.forEach(error => {
          errorText += error + '<br>'
        })

        setLoadingModalText(errorText)
      }

      addToCart()
        .catch(error => {})
        .then(() => {})
        .finally(() => {
          setLoadingModalText((currentLoadingModalText) => {
            if (currentLoadingModalText === '') {
              setShowLoadingModal(false)
            }

            return currentLoadingModalText
          })
        })
    }
  }, [])

  useEffect(() => {
    cart.validateContent()

    // Google Analytics (GA4)
    let eventGroups = cart.eventGroups
    GA4Tracking.pushEvent(
      'view_cart',
      GA4Tracking.getItemsAndAddonsFromCartEventGroups(cart.eventGroups)
    )
  }, [])

  const renderLoadingModal = () => {
    if (! showLoadingModal) {
      return null
    }

    return (
      <div className={'mtt-modal_2 mtt-modal--small'}>
        <div className={'mtt-modal__overlay'} onClick={() => setShowLoadingModal(false)}>
          <div className='mtt-modal__container'>
            <div className='mtt-modal__title'>
              {Translator.translate('Tilføjer arrangement til kurven', 'cart')}
            </div>

            <div
              className='mtt-modal__text'
              dangerouslySetInnerHTML={{ __html: loadingModalText }}
            />
          </div>
        </div>
      </div>
    )
  }

  if (cart.eventGroups.length === 0) {
    return (
      <div>
        <div className='mtt-cart-wrap'>
          <div className='page_container'>
            <div className='mtt-cart-container'>
              <div className='mtt-cart-content'>
                <CartNavigation step={0}/>
                <div className='mtt-cart-content__title'>
                  <h1 className='mtt-cart-title'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='21.42'
                      height='24'
                      viewBox='0 0 17.846 20'
                    >
                      <path
                        d='M24.471,37h10.9a3.475,3.475,0,0,0,3.471-3.471V23.154a.617.617,0,0,0-.615-.615H34.538v-.923a4.615,4.615,0,1,0-9.231,0v.923H21.615a.617.617,0,0,0-.615.615V33.529A3.475,3.475,0,0,0,24.471,37Zm2.068-15.385a3.385,3.385,0,0,1,6.769,0v.923H26.538Zm-4.308,2.154h3.077v1.538a.615.615,0,1,0,1.231,0V23.769h6.769v1.538a.615.615,0,1,0,1.231,0V23.769h3.077v9.76a2.244,2.244,0,0,1-2.24,2.24h-10.9a2.244,2.244,0,0,1-2.24-2.24Z'
                        transform='translate(-21 -17)'
                        fill='currentColor'
                      />
                    </svg>
                    {Translator.translate('Kurv', 'cart-navigation')}
                  </h1>
                </div>
                <div className={'mtt-cart-navigation__text'}>
                  {Translator.translate(
                    'Du har endnu ikke tilføjet arrangementer til kurven',
                    'cart'
                  )}
                </div>
              </div>
              {renderLoadingModal()}
              <CartOverview
                url={Settings.getSetting('payment_address_url', 'cart-navigation')}
                isLinkDisabled={!StepController.getInstance().canGoToPaymentAddress}
                validateStep={()=>!StepController.getInstance().validateCanGoToPaymentAddress()}
                buttonText={Translator.translate('Gå til betalingsadresse', 'cart')}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div>
      <div className='mtt-cart-wrap'>
        <div className='page_container'>
          <div className='mtt-cart-container'>
            <div className='mtt-cart-content'>
              <CartNavigation step={0}/>
              <CartContentTitle />
              {renderLoadingModal()}
              <CartContentProducts />
              <div className='mtt-cart-footer-navigation single-button'>
                <button
                  className={'button lightgrey smaller'}
                  onClick={() => {
                    setShowModal(true)
                  }}
                >
                  {Translator.translate('Tøm kurv', 'cart')}
                </button>
                {showModal ? (
                  <div
                    className={
                      'mtt-modal_2 mtt-modal--small mtt-modal--visible2'
                    }
                  >
                    <div
                      className={'mtt-modal__overlay'}
                      onClick={e => {
                        setShowModal(false)
                      }}
                    >
                      <div className='mtt-modal__container'>
                        <div className='mtt-modal__title'>
                          {Translator.translate(
                            'Tøm kurven',
                            'cart'
                          )}
                        </div>
                        <div className='mtt-modal__text'>
                          {Translator.translate(
                            'Er du sikker på at du vil tømme kurven?',
                            'cart'
                          )}
                        </div>
                        <div className='mtt-modal__buttons'>
                          <a
                            href='#'
                            className='button'
                            onClick={e => {
                              e.preventDefault()
                              setShowModal(false)
                            }}
                          >
                            {Translator.translate('Annullér', 'cart')}
                          </a>
                          <button
                            className='button blue'
                            onClick={() => {
                              let cart = RootStore.getInstance().cart
                              cart.empty()
                              setShowModal(false)
                            }}
                          >
                            {Translator.translate('Tøm kurv', 'cart')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <CartOverview
              url={Settings.getSetting('payment_address_url', 'cart-navigation')}
              isLinkDisabled={!StepController.getInstance().canGoToPaymentAddress}
              validateStep={()=>!StepController.getInstance().validateCanGoToPaymentAddress()}
              buttonText={Translator.translate('Gå til betalingsadresse', 'cart')}
            />
          </div>
        </div>
      </div>
    </div>
  )
})
ReactRenderer.registerComponent('Cart', CartView)
ReactRenderer.render()
